import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { prefetchProduct } from "@/lib/prefetch";
import { saveLastClickedProduct } from "@/lib/scrollMemory";
import SmartImage, { resolveImageSrc } from "@/components/SmartImage";
import { imageUrl, imageSrcSet } from "@/lib/image";

/**
 * Myntra-style product card:
 *  - Aggressive pricing: current + strikethrough MRP + green discount %
 *  - Rating pill overlay on image (when rating > 0)
 *  - Wishlist heart (top-right) — reads from WishlistContext so PLP
 *    hearts render as filled without a per-card API roundtrip
 *  - Brand name bold uppercase, product name in muted ink-soft
 */
const ProductCard = ({ product, index = 0 }) => {
  const [hover, setHover] = useState(false);
  // The alternate photo is only fetched once the shopper hovers (never on touch),
  // and via the resized /img/ path — it used to load a full 1000px file per card.
  const [seenHover, setSeenHover] = useState(false);
  const { user } = useAuth();
  const { has, toggle } = useWishlist();
  const wishlisted = has(product);

  const _saleNum = parseFloat(product.sale_price);
  const _priceNum = parseFloat(product.price);
  const _mrpNum = parseFloat(product.regular_price);
  const _effectivePrice =
    Number.isFinite(_saleNum) && _saleNum > 0 ? _saleNum : _priceNum;
  const onSale =
    Number.isFinite(_mrpNum) && Number.isFinite(_effectivePrice) && _mrpNum > _effectivePrice;
  const discountPct = onSale ? Math.round(((_mrpNum - _effectivePrice) / _mrpNum) * 100) : 0;

  const prefetch = () => prefetchProduct(product.slug);
  const images = product.images || [];
  const primary = images[0] || product.image;
  const secondary = images[1] || null;

  const onHeartClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product);
  };

  const rating = parseFloat(product.average_rating || product.rating || 0);
  const reviewCount = parseInt(product.rating_count || product.review_count || 0, 10);

  return (
    <>
      <Link
        to={`/product/${product.slug}`}
        className="group block fade-up bg-white"
        style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
        onMouseEnter={() => {
          setHover(true);
          setSeenHover(true);
          prefetch();
        }}
        onMouseLeave={() => setHover(false)}
        onFocus={prefetch}
        onTouchStart={prefetch}
        onClick={() => {
          try {
            const memoryKey = `cat:${window.location.pathname}${window.location.search}`;
            saveLastClickedProduct(product.slug, memoryKey);
          } catch {
            /* ignore */
          }
        }}
        data-testid={`product-card-${product.slug}`}
        data-product-slug={product.slug}
      >
        <div className="product-img-wrap relative overflow-hidden">
          <SmartImage
            src={primary}
            alt={product.name}
            width={600}
            height={800}
            eager={index < 4}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              secondary && hover ? "opacity-0" : "opacity-100"
            }`}
          />
          {secondary && seenHover && (
            <img
              src={imageUrl(resolveImageSrc(secondary), 600)}
              srcSet={imageSrcSet(resolveImageSrc(secondary))}
              sizes="(min-width: 1280px) 22vw, (min-width: 768px) 32vw, 48vw"
              alt={`${product.name} alternate`}
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                hover ? "opacity-100" : "opacity-0"
              }`}
            />
          )}

          {/* Wishlist heart — toggles /me/wishlist on click. Redirects
              to /login (with next=) when the shopper isn't authenticated. */}
          <button
            onClick={onHeartClick}
            aria-label={wishlisted ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
            data-testid={`wishlist-heart-${product.slug}`}
            className={`absolute top-2.5 right-2.5 z-10 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center transition-opacity ${
              wishlisted ? "opacity-100 text-pink" : "opacity-0 group-hover:opacity-100 hover:text-pink"
            }`}
          >
            <Heart className="w-4 h-4" strokeWidth={1.75} fill={wishlisted ? "currentColor" : "none"} />
          </button>

          {/* Rating pill (bottom-left of image) */}
          {rating > 0 && (
            <div className="absolute bottom-2 left-2 bg-white rounded-sm shadow-sm px-2 py-0.5 flex items-center gap-1 text-[11px] font-semibold text-ink">
              <span>{rating.toFixed(1)}</span>
              <span className="text-greenbrand text-[13px] leading-none">★</span>
              {reviewCount > 0 && (
                <>
                  <span className="w-px h-3 bg-ink-faint" />
                  <span className="text-ink-mute font-normal">
                    {reviewCount >= 1000 ? `${(reviewCount / 1000).toFixed(1)}k` : reviewCount}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="pt-2 pb-3 px-2.5">
          <p className="brand-text text-[13px] line-clamp-1">
            {product.derived_brand || product.categories?.[0]?.name || "Luxsova"}
          </p>
          <h3 className="text-[13px] text-ink-soft line-clamp-1 mt-0.5 group-hover:text-ink transition-colors">
            {product.name}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="price-current whitespace-nowrap">
              Rs. {Math.round(_effectivePrice).toLocaleString("en-IN")}
            </span>
            {onSale && (
              <>
                <span className="price-strike whitespace-nowrap">
                  Rs. {Math.round(_mrpNum).toLocaleString("en-IN")}
                </span>
                <span className="price-discount whitespace-nowrap">({discountPct}% OFF)</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </>
  );
};

export default ProductCard;
